import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import clsx from 'clsx';
import axiosInstance from '../../../config/axiosConfig';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputTextarea } from 'primereact/inputtextarea';
import useSelectedCompanyStore from '../../../store/useSelectedCompanyStore';
import useCampaignStore from '../../../store/useCampaignStore';

const CampaignEdit = () => {
    const [campaignData, setCampaignData] = useState(null);
    const [localInput, setLocalInput] = useState({
        campaign_name: '',
        language: '',
        recruiter: '',
    });
    const [isValid, setIsValid] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersRequestLoading, setUsersRequestLoading] = useState(false);
    const [tests, setTests] = useState([]);
    const [expanded, setExpanded] = useState({});

    const history = useHistory();

    const { campaign_id } = useParams();

    const toast = useRef(null);
    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);
    const { addCampaign, deleteCampaign } = useCampaignStore((state) => ({
        addCampaign: state.addCampaign,
        deleteCampaign: state.deleteCampaign,
    }));

    useEffect(() => {
        fetchCampaignData();
        fetchUsers();
        fetchTests();
    }, [campaign_id]);

    const fetchCampaignData = async () => {
        try {
            const response = await axiosInstance.get(`/dashboard/campaign/${selectedCompany?.company_id}/${campaign_id}/getDetails`);
            const { campaign, recruiter } = response.data;

            setCampaignData(campaign);
            setLocalInput({
                campaign_name: campaign.campaign_name || '',
                language: campaign.language || 'Choisir une langue',
                recruiter: recruiter.user_id || '',
            });
        } catch (error) {
            console.error('Failed to fetch campaign data:', error);
        }
    };

    const fetchUsers = async () => {
        setUsersRequestLoading(true);
        try {
            const response = await axiosInstance.get(`/dashboard/companies/${selectedCompany?.company_id}/users/list`);
            const items = response.data.items.map(item => ({
                label: item.display_name || `${item.first_name} ${item.last_name || ''}`,
                first_name: item.first_name,
                last_name: item.last_name,
                value: item.user_id,
                avatar: item.profile_picture ? `/dashboard/user/avatar/${item.profile_picture}` : null,
            }));
            setUsers(items);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setUsersRequestLoading(false);
        }
    };

    const fetchTests = async () => {
        try {
            const response = await axiosInstance.get('/api/v1/tests?page=1&limit=1000');
            setTests(response.data.data.items);
        } catch (error) {
            console.error('Failed to fetch tests:', error);
        }
    };

    const handleLocalChange = (key, value) => {
        setLocalInput(prev => ({ ...prev, [key]: value }));
    };

    const validateInputs = () => {
        const is_valid = Object.values(localInput).every(value => value && value !== 'Choisir une langue');
        setIsValid(is_valid);
    };

    useEffect(() => {
        validateInputs();
    }, [localInput]);

    const handleSubmit = () => {
        setSubmitLoading(true);

        const updatedCampaign = {
            ...campaignData,
            campaign_name: localInput.campaign_name,
            language: localInput.language,
            recruiter: localInput.recruiter,
        };

        axiosInstance.put(`/dashboard/campaign/${selectedCompany?.company_id}/${campaign_id}/update`, updatedCampaign).then(response => {
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Campagne modifiée avec succès !', life: 10000 });
            addCampaign(response.data.campaign);
            setSubmitLoading(false);
            setIsValid(false);
            setTimeout(() => {
                history.push(`/dashboard/campaigns/${campaign_id}/candidates`);
            }, 2000);
        }).catch(error => {
            console.error('Error updating campaign:', error);
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la modification de la campagne', life: 10000 });
            setSubmitLoading(false);
        });
    };

    const handleDelete = () => {
        // Handle delete logic here
    };

    const handleArchive = () => {
        confirmDialog({
            message: 'Êtes-vous sûr de vouloir archiver cette campagne?',
            header: 'Confirmer l\'archivage',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Oui',
            rejectLabel: 'Non',
            accept: () => {
                archiveCampaign();
            },
            reject: () => {
                // Do nothing
            }
        });
    };

    const archiveCampaign = () => {
        axiosInstance.post(`/dashboard/campaign/${campaign_id}/archive`)
            .then(response => {
                toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Campagne archivée avec succès !', life: 10000 });

                setTimeout(() => {
                    window.location.href = "/dashboard/campaigns";
                }, 2000);
            })
            .catch(error => {
                console.error('Error archiving campaign:', error);
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de l\'archivage de la campagne', life: 10000 });
            });
    };

    const userTemplate = (option) => (
        <div className="is-flex is-align-items-center">
            {option.avatar ? (
                <Avatar
                    image={`/dashboard/user/avatar/${option.avatar}`}
                    className="m-2"
                    size="large"
                    shape="circle"
                />
            ) : (
                <div className="deepsy-anonymous-avatar m-2">
                    <span className='has-text-dgrad-deepsy'>{option.first_name[0].toUpperCase()}{option.last_name[0].toUpperCase()}</span>
                </div>
            )}

            <span>{option.label}</span>
        </div>
    );

    const toggleExpand = (id) => {
        setExpanded(prevExpanded => ({ ...prevExpanded, [id]: !prevExpanded[id] }));
    };

    const questionTypeOptions = [
        { label: 'Choix multiples', value: 'mcq-m' },
        { label: 'Choix unique', value: 'mcq-u' },
        { label: 'Réponse courte', value: 'short' },
        { label: 'Réponse longue', value: 'long' },
        { label: 'Choix unique (Likert)', value: 'likert' },
    ];

    const renderQuestions = (questions) => (
        questions.map((question, questionIndex) => (
            <div key={question.id || questionIndex} className="box radius-10 mt-2">
                <p><strong>Question {questionIndex + 1} :</strong> {question.question}</p>
                <p><strong>Type :</strong> {questionTypeOptions.find(option => option.value === question.type)?.label || ''}</p>
                {['mcq-m', 'mcq-u'].includes(question.type) && question.choices && question.choices.length > 0 && (
                    <div>
                        <p><strong>Choix :</strong></p>
                        <ul>
                            {question.choices.map((choice, choiceIndex) => (
                                <li key={`mcq-choice-${choiceIndex}`}>{choice}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {question.type === 'likert' && question.choices && question.choices.length > 0 && (
                    <div className="likert-scale-container">
                        <table className="table is-striped is-hoverable is-fullwidth">
                            <thead className='has-text-centered'>
                                <tr>
                                    <th>Question</th>
                                    <th>Strongly Disagree</th>
                                    <th>Disagree</th>
                                    <th>Neutral</th>
                                    <th>Agree</th>
                                    <th>Strongly Agree</th>
                                </tr>
                            </thead>
                            <tbody>
                                {question.choices.map((choice, choiceIndex) => (
                                    <tr key={choiceIndex}>
                                        <td><InputTextarea autoResize className='p-2' value={choice} rows={3} cols={30} readOnly /></td>
                                        {[1, 2, 3, 4, 5].map((num) => (
                                            <td key={num} style={{ textAlign: 'center' }}>
                                                <input
                                                    type="radio"
                                                    name={`likert-${question.id}-${choiceIndex}`}
                                                    value={num}
                                                    disabled
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        ))
    );

    if (!campaignData) return <p>Loading...</p>;

    return (
        <section id="step-recap" className="container section">
            <Toast ref={toast} position="bottom-right" />
            <ConfirmDialog />

            <h2 className="title">Modifier la Campagne</h2>

            <div className="box recap-section">
                <div className="field">
                    <label className="label">Nom de la campagne</label>
                    <input
                        className="input"
                        type="text"
                        value={localInput.campaign_name}
                        onChange={(e) => handleLocalChange('campaign_name', e.target.value)}
                    />
                </div>

                <div className="field">
                    <label className="label">Langue</label>
                    <Dropdown
                        className="is-fullwidth"
                        value={localInput.language}
                        options={[{ label: '🇫🇷 Français', value: 'fr' }, { label: '🇬🇧 English', value: 'en' }]}
                        onChange={(e) => handleLocalChange('language', e.value)}
                    />
                </div>

                <div className="field">
                    <label className="label">Responsable en charge de la campagne</label>
                    {usersRequestLoading ? (
                        <Dropdown className="is-fullwidth" placeholder="Chargement..." disabled />
                    ) : (
                        <Dropdown
                            className="is-fullwidth"
                            value={localInput.recruiter}
                            options={users}
                            onChange={(e) => handleLocalChange('recruiter', e.value)}
                            itemTemplate={userTemplate}
                        />
                    )}
                </div>
            </div>

            <hr />

            <h2 className="title">Questions de Pré-qualification</h2>

            {campaignData.questions && campaignData.questions.length > 0 ? (
                campaignData.questions.map((page, pageIndex) => (
                    <div key={page.id || pageIndex} className="box radius-20">
                        <div className="media-content">
                            <p onClick={() => toggleExpand(page.id || pageIndex)} className="is-clickable">
                                <strong>
                                    Page - {pageIndex + 1} {page.title}
                                    <span className="icon is-small is-pulled-right">
                                        <i className={`fas fa-chevron-${expanded[page.id || pageIndex] ? 'down' : 'right'}`}></i>
                                    </span>
                                </strong>
                            </p>
                            {expanded[page.id || pageIndex] && renderQuestions(page.questions)}
                        </div>
                    </div>
                ))
            ) : (
                <p>Aucune question de pré-qualification.</p>
            )}

            <hr />

            <h2 className="subtitle">Tests Sélectionnés</h2>
            <div className="tags">
                {campaignData.choosen_tests.map((test, index) => {

                    const fullTest = tests.find(t => t.ref === test.ref);
                    const testColor = fullTest?.test_color && fullTest.test_color !== '#FFFFFF' ? fullTest.test_color : '#4786C6';
                    const gradientColor = testColor + '63';
                    return (
                        <Tag
                            key={index}
                            className="selected_test__tag is-clickable mt-1"
                            style={{ background: `linear-gradient(90deg, ${testColor}, ${gradientColor})`, }}
                        >
                            <span>{fullTest?.title || test.ref}</span>
                        </Tag>
                    );
                })}
            </div>

            <div className="buttons is-justify-content-center mt-4">
                <button
                    className={clsx('button is-primary is-light is-outlined is-large is-responsive mt-6 mb-6', { 'is-loading': submitLoading })}
                    onClick={handleSubmit}
                    disabled={!isValid}
                >
                    Sauvegarder
                </button>
                <button className="button is-warning is-light is-outlined is-large is-responsive mt-6 mb-6" onClick={handleArchive}>
                    Archiver la campagne
                </button>
                <button className="button is-danger is-light is-outlined is-large is-responsive mt-6 mb-6" onClick={handleDelete} disabled>
                    Supprimer la campagne
                </button>
            </div>
        </section>
    );
};

export default CampaignEdit;